import { useState } from "react"
import { ChevronDown } from "lucide-react"

const faqData = [
  {
    id: 1,
    question: "Can I change plans anytime?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing differences."
  },
  {
    id: 2,
    question: "What happens if I exceed my usage limits?",
    answer: "Your workflows will continue to run, and you'll be charged for additional usage at our standard rates. You can set spending limits to control costs."
  },
  {
    id: 3,
    question: "Do you offer annual discounts?",
    answer: "Yes! Save 20% when you pay annually. Contact our sales team for custom enterprise pricing and longer-term commitments."
  },
  {
    id: 4,
    question: "Is there a free trial for paid plans?",
    answer: "All paid plans come with a 14-day free trial. No credit card required to start, and you can cancel anytime during the trial period."
  },
  {
    id: 5,
    question: "What integrations are available?",
    answer: "We offer thousands of integrations including OpenAI, Google Cloud, AWS, Anthropic, Stability AI, webhooks, databases, and custom APIs. New integrations are added regularly."
  },
  {
    id: 6,
    question: "How secure is my data?",
    answer: "We use enterprise-grade security with encryption at rest and in transit, SOC 2 compliance, and regular security audits. Your workflows and data are completely isolated and private."
  }
]

export default function FAQSection() {
  const [openItems, setOpenItems] = useState<number[]>([])

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    )
  }

  return (
    <div style={{ background: "url(https://images.pexels.com/photos/4175060/pexels-photo-4175060.jpeg) center left" }}>
    <section className="backdrop-blur-lg py-16" id="faq">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions? We've got answers. Can't find what you're looking for? Contact our support team.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqData.map((faq) => {
            const isOpen = openItems.includes(faq.id)
            return (
              <div
                key={faq.id}
                className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset transition-colors duration-200"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        isOpen ? "transform rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>
                
                <div
                  className={`px-6 bg-gray-50 border-t border-gray-100 transition-all duration-300 ease-in-out ${
                    isOpen 
                      ? "py-4 opacity-100 max-h-40" 
                      : "py-0 opacity-0 max-h-0 overflow-hidden"
                  }`}
                >
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Still have questions?
          </p>
          <a
            href="/help"
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors duration-200"
          >
            Contact Support
          </a>
        </div>
      </div>
    </section>
    </div>
  )
}
